// Research Guide hub: section definitions + article grouping.
// Shared by ResearchPeptideGuide.jsx (client) and scripts/prerender.js (static HTML).
// Articles always come from the public /api/blog data; this file only classifies them.

const HUB_PATH = '/research-peptide-guide';

const HUB_SECTIONS = [
  {
    id: 'metabolic-glp1-research',
    title: 'Metabolic / GLP-1 research',
    navLabel: 'Metabolic / GLP-1',
    description: 'Background on incretin and amylin receptor research compounds such as semaglutide, tirzepatide, retatrutide and cagrilintide, including published comparisons of how they are studied in laboratory settings.',
    match: /glp|semaglutide|tirzepatide|retatrutide|cagrilintide|incretin|lemon-bottle|aod-9604|metabolic/,
    links: [
      { href: '/glp1-research', label: 'GLP-1 research peptides overview', cta: 'Read overview' },
      { href: '/tirzepatide-research', label: 'Tirzepatide research reference', cta: 'Read reference' },
      { href: '/weight-management', label: 'Metabolic research peptides', cta: 'Browse products' },
    ],
  },
  {
    id: 'tissue-copper-peptide-research',
    title: 'Tissue and copper-peptide research',
    navLabel: 'Tissue & copper peptides',
    description: 'Research overviews of BPC-157, TB-500 (thymosin beta-4 fragment) and the copper peptide GHK-Cu, and how these compounds are compared in preclinical literature.',
    match: /bpc-157|tb-500|thymosin|ghk|copper|tissue/,
    links: [
      { href: '/tissue-recovery', label: 'Regenerative research peptides', cta: 'Browse products' },
    ],
  },
  {
    id: 'growth-hormone-research',
    title: 'Growth hormone research',
    navLabel: 'Growth hormone',
    description: 'Articles on growth-hormone-axis research peptides such as CJC-1295 and Ipamorelin, covering how these compounds are characterized and compared in laboratory studies.',
    match: /cjc-1295|ipamorelin|tesamorelin|sermorelin|ghrp|growth-hormone/,
    links: [
      { href: '/growth-hormone', label: 'Growth hormone research peptides', cta: 'Browse products' },
    ],
  },
  {
    id: 'cognitive-neurological-research',
    title: 'Cognitive and neurological research',
    navLabel: 'Cognitive & neurological',
    description: 'Summaries of what published preclinical work reports on neuroactive research peptides such as Selank, including the limits of the current evidence.',
    match: /selank|semax|cognitive|nootropic|dihexa|neuro/,
    links: [
      { href: '/cognitive-neuro', label: 'Cognitive and neuro research peptides', cta: 'Browse products' },
    ],
  },
  {
    id: 'testing-certificates-of-analysis',
    title: 'Testing and Certificates of Analysis',
    navLabel: 'Testing & CoAs',
    description: 'How to read a Certificate of Analysis, what purity and identity testing show, what research-use-only labeling means, and where to look up batch documentation.',
    match: /certificate-of-analysis|purity-testing|product-label|research-use-only/,
    links: [
      { href: '/coa', label: 'Certificate of Analysis library', cta: 'Browse CoAs' },
      { href: '/quality', label: 'Quality and testing standards', cta: 'Read standards' },
    ],
  },
  {
    id: 'storage-handling',
    title: 'Laboratory storage and handling',
    navLabel: 'Storage & handling',
    description: 'What affects peptide integrity in a laboratory: temperature, light, moisture and time, and why consistent storage conditions matter for reproducible research.',
    match: /storage|stability/,
    links: [
      { href: '/lab-supplies', label: 'Laboratory supplies', cta: 'Browse supplies' },
    ],
  },
];

// Earlier entries win when an article matches more than one section.
const MATCH_ORDER = [
  'testing-certificates-of-analysis', 'storage-handling', 'growth-hormone-research',
  'cognitive-neurological-research', 'tissue-copper-peptide-research', 'metabolic-glp1-research',
];

const cleanTitle = (title) => String(title || '').replace(/^\s*#+\s*/, '').trim();

const articleCta = (a) => {
  if (/-vs-/.test(a.slug || '') || a.category === 'comparison') return 'Read comparison';
  if (['education', 'guide'].includes(a.category)) return 'Read guide';
  return 'Read research';
};

function groupArticles(articles) {
  const groups = { intro: [] };
  HUB_SECTIONS.forEach((s) => { groups[s.id] = []; });
  const seen = new Set();
  (articles || []).forEach((a) => {
    if (!a || !a.slug || seen.has(a.slug)) return;
    seen.add(a.slug);
    const slug = a.slug.toLowerCase();
    const id = MATCH_ORDER.find((sid) => HUB_SECTIONS.find((s) => s.id === sid).match.test(slug));
    const item = { slug: a.slug, title: cleanTitle(a.title), excerpt: a.excerpt || '', cta: articleCta(a) };
    groups[id || 'intro'].push(item);
  });
  return groups;
}

module.exports = { HUB_PATH, HUB_SECTIONS, groupArticles };
